import { useState } from 'react';
import { Tag, Plus } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

export const LabelChips = ({ labels = [], testId }) => (
  <span className="inline-flex flex-wrap gap-1" data-testid={testId}>
    {labels.map((l) => (
      <span key={l} className="text-[11px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">{l}</span>
    ))}
  </span>
);

export const DocumentLabelEditor = ({ file, options, index, onSaved }) => {
  const [custom, setCustom] = useState('');
  const [saving, setSaving] = useState(false);
  const current = file.labels || [];

  const save = async (next) => {
    setSaving(true);
    try {
      const r = await api.put(`/storage/files/${file.file_id}/labels`, { labels: next });
      onSaved(file.file_id, r.data.labels);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update labels');
    } finally { setSaving(false); }
  };
  const toggle = (l) => save(current.includes(l) ? current.filter((x) => x !== l) : [...current, l]);
  const addCustom = () => {
    const l = custom.trim();
    if (!l) return;
    setCustom('');
    if (!current.some((x) => x.toLowerCase() === l.toLowerCase())) save([...current, l]);
  };
  const all = [...new Set([...options, ...current])];

  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <LabelChips labels={current} testId={`care-documents-labels-${index}`} />
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
            data-testid={`care-documents-label-btn-${index}`}>
            <Tag className="h-3 w-3" /> {current.length ? 'Edit' : 'Add label'}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-60 p-3 space-y-2" data-testid={`care-documents-label-popover-${index}`}>
          {all.map((l) => (
            <label key={l} className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={current.includes(l)} disabled={saving} onCheckedChange={() => toggle(l)}
                data-testid={`care-label-option-${l.toLowerCase().replace(/\s+/g, '-')}`} />
              {l}
            </label>
          ))}
          <div className="flex gap-1 pt-1">
            <Input value={custom} maxLength={30} placeholder="New label" className="h-8 text-sm"
              onChange={(e) => setCustom(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addCustom()}
              data-testid="care-label-custom-input" />
            <Button type="button" size="sm" variant="outline" className="h-8" disabled={saving || !custom.trim()}
              onClick={addCustom} data-testid="care-label-custom-add"><Plus className="h-3.5 w-3.5" /></Button>
          </div>
          {current.length >= 6 && <p className="text-[11px] text-muted-foreground">Up to 6 labels per document.</p>}
        </PopoverContent>
      </Popover>
    </span>
  );
};
